// ─── src/App.tsx ──────────────────────────────────────────────────────────────
import { useState, useCallback, useMemo } from "react";
import {
  Download, Menu,
  Users, GraduationCap, UserCog, ClipboardList,
  CalendarClock, UserCheck, MessageSquare,
  Ticket, Handshake, Megaphone,
  Award, Landmark, Rocket,
  Target, MapPin,
  Mail, FileText,
  BarChart3, FlaskConical,
  MailCheck,
  KeyRound,
  Radio, MessageSquareQuote,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth }             from "./auth/AuthContext";
import { useRegistrations, registrationKeys } from "./hooks/useRegistrations";
import { useRegistrationStats, statsKeys }    from "./hooks/useStats";
import { getRegistrationStats }               from "./api/stats";
import { getRegistrations }                   from "./api/registration";
import { Header }              from "./components/layout/Header";
import { StatsOverview }       from "./components/dashboard/StatsOverview";
import { FilterBar }           from "./components/dashboard/FilterBar";
import type { Filters }        from "./components/dashboard/FilterBar";
import { ApplicantsTable }     from "./components/dashboard/ApplicantsTable";
import { Pagination }          from "./components/dashboard/Pagination";
import {
  CityChart, GenderChart, IdeaChart, ScholarshipChart, IdeaThemeChart,
} from "./components/dashboard/Charts";
import { TableSkeleton }       from "./components/ui/Skeleton";
import { EmptyState }          from "./components/ui/EmptyState";
import type { Registration, RegistrationsParams } from "./types/registration";
import type { RegistrationStatsResponse }         from "./types/stats";
import { toChartEntries }                         from "./types/stats";
import MentorsView           from "./MentorsView";
import NewsletterView        from "./NewsletterView";
import ReportLeadsView       from "./ReportLeadsView";
import CommunityCatalystsView from "./CommunityCatalystsView";
import EcosystemPerceptionView from "./EcosystemPerceptionView";
import FnfSurveyView          from "./FnfSurveyView";
import CatalystWaitlistView    from "./CatalystWaitlistView";
import MeetupProposalsView     from "./MeetupProposalsView";
import MeetupAttendeesView     from "./MeetupAttendeesView";
import MeetupFeedbacksView     from "./MeetupFeedbacksView";
import ChartLabView          from "./ChartLabView";
import CatalystPortalView    from "./CatalystPortalView";
import GewParticipantsView   from "./GewParticipantsView";
import GewPartnersView       from "./GewPartnersView";
import GewPartnerApplicationsView from "./GewPartnerApplicationsView";
import GewOverviewView       from "./GewOverviewView";
import GewSponsorsView       from "./GewSponsorsView";
import AwardsNominationsView from "./AwardsNominationsView";
import AwardsInvitationsView from "./AwardsInvitationsView";
import DoorCodesView from "./DoorCodesView";
import InvestorLoungeFoundersView  from "./InvestorLoungeFoundersView";
import InvestorLoungeInvestorsView from "./InvestorLoungeInvestorsView";
import NpdRsvpsView         from "./NpdRsvpsView";
import NpdWaitlistView      from "./NpdWaitlistView";
import NpdRegistrationsView from "./NpdRegistrationsView";
import ElevateApplicationsView from "./ElevateApplicationsView";
import ElevateFeedbackView from "./ElevateFeedbackView";
import GewFeedbackView from "./GewFeedbackView";
import GewLiveView from "./GewLiveView";
import { Sidebar, type SidebarGroup } from "./components/dashboard/Sidebar";
import type { MeetupEvent } from "./types/meetupEvent";

type ActiveView =
  | "applicants"
  | "mentors"
  | "newsletter"
  | "report-leads"
  | "community-catalysts"
  | "ecosystem-perception"
  | "fnf-survey"
  | "catalyst-waitlist"
  | "meetup-proposals"
  | "meetup-attendees"
  | "meetup-feedbacks"
  | "gew-overview"
  | "gew-feedback"
  | "gew-live"
  | "gew-participants"
  | "gew-partners"
  | "gew-partner-applications"
  | "gew-sponsors"
  | "door-codes"
  | "awards-nominations"
  | "awards-invitations"
  | "investor-lounge-founders"
  | "investor-lounge-investors"
  | "npd-rsvps"
  | "npd-waitlist"
  | "npd-registrations"
  | "elevate-applications"
  | "elevate-feedback"
  | "chart-lab";

/**
 * Sidebar grouping. Adding a new view is a one-line change here — no need
 * to hunt through App.tsx's render block. Groups scale to 25+ items before
 * the sidebar itself needs another design pass.
 */
const NAV_GROUPS: SidebarGroup<ActiveView>[] = [
  {
    label: "Recruits",
    emoji: "📥",
    items: [
      { key: "applicants",         label: "Applicants",         icon: Users },
      { key: "mentors",            label: "Mentors",            icon: GraduationCap },
      { key: "community-catalysts", label: "Catalysts",         icon: UserCog },
      { key: "catalyst-waitlist",  label: "Catalyst Waitlist",  icon: ClipboardList },
    ],
  },
  {
    label: "Meetups",
    emoji: "🤝",
    items: [
      { key: "meetup-proposals", label: "Proposals", icon: CalendarClock },
      { key: "meetup-attendees", label: "Attendees", icon: UserCheck },
      { key: "meetup-feedbacks", label: "Feedback",  icon: MessageSquare },
    ],
  },
  {
    label: "GEW & Conference",
    emoji: "🎪",
    items: [
      { key: "gew-overview",             label: "Overview",              icon: BarChart3 },
      { key: "gew-live",                 label: "GEW Live",              icon: Radio },
      { key: "gew-feedback",             label: "Event Feedback",        icon: MessageSquare },
      { key: "gew-participants",         label: "Participants",          icon: Ticket },
      { key: "gew-partners",             label: "Partners (legacy)",     icon: Handshake },
      { key: "gew-partner-applications", label: "Partner Applications",  icon: Handshake },
      { key: "gew-sponsors",             label: "Sponsors",              icon: Megaphone },
      { key: "door-codes",               label: "Door Codes",            icon: KeyRound },
    ],
  },
  {
    label: "Awards",
    emoji: "🏆",
    items: [
      { key: "awards-nominations", label: "Nominations", icon: Award },
      { key: "awards-invitations", label: "Ceremony Invitations", icon: MailCheck },
    ],
  },
  {
    label: "Investor Lounge",
    emoji: "🥃",
    items: [
      { key: "investor-lounge-founders",  label: "Founder Applications",  icon: Rocket },
      { key: "investor-lounge-investors", label: "Investor Applications", icon: Landmark },
    ],
  },
  {
    label: "National Pitching Day",
    emoji: "🎯",
    items: [
      { key: "npd-rsvps",         label: "Investor RSVPs",      icon: Target },
      { key: "npd-registrations", label: "Founder Registrations", icon: Rocket },
      { key: "npd-waitlist",      label: "2027 Waitlist",       icon: MapPin },
    ],
  },
  {
    label: "Elevate · Damascus 2026",
    emoji: "🚀",
    items: [
      { key: "elevate-applications", label: "Mentor Applications", icon: Rocket },
      { key: "elevate-feedback",     label: "Elevate Feedback",    icon: MessageSquareQuote },
    ],
  },
  {
    label: "Communications",
    emoji: "📨",
    items: [
      { key: "newsletter",   label: "Newsletter",   icon: Mail },
      { key: "report-leads", label: "Report Leads", icon: FileText },
    ],
  },
  {
    label: "Insights",
    emoji: "📊",
    items: [
      { key: "ecosystem-perception", label: "Ecosystem",  icon: BarChart3 },
      { key: "fnf-survey",           label: "FNF Survey", icon: ClipboardList },
      { key: "chart-lab",            label: "Chart Lab",  icon: FlaskConical },
    ],
  },
];

const LIMIT = 10;

// ── CSV export ─────────────────────────────────────────────────────────────────
const CSV_COLUMNS: { label: string; key: keyof Registration }[] = [
  { label: "Full Name",              key: "fullName" },
  { label: "Email",                  key: "email" },
  { label: "Phone",                  key: "phone" },
  { label: "Gender",                 key: "gender" },
  { label: "Age",                    key: "age" },
  { label: "City",                   key: "city" },
  { label: "LinkedIn URL",           key: "linkedInUrl" },
  { label: "Primary Role",           key: "primaryRole" },
  { label: "Years of Experience",    key: "yearsOfExperience" },
  { label: "Current Status",         key: "currentStatus" },
  { label: "Is Pitching Idea",       key: "isPitchingIdea" },
  { label: "Idea Title",             key: "ideaTitle" },
  { label: "Idea Theme",             key: "ideaTheme" },
  { label: "Problem Solving",        key: "problemSolving" },
  { label: "Target Users",           key: "targetUsers" },
  { label: "Current Stage",          key: "currentStage" },
  { label: "Available All Days",     key: "availableAllDays" },
  { label: "Requesting Scholarship", key: "requestingScholarship" },
  { label: "Why Scholarship",        key: "whyScholarship" },
  { label: "Why This Event",         key: "whyThisEvent" },
  { label: "App Status",             key: "status" },
  { label: "Registered At",          key: "createdAt" },
];

function exportToCsv(rows: Registration[]) {
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = CSV_COLUMNS.map((c) => c.label).join(",");
  const body   = rows.map((r) =>
    CSV_COLUMNS.map((c) => escape(r[c.key])).join(",")
  );
  const csv  = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `applicants_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Client-side filter used only for CSV export (runs against on-demand full fetch)
const applyFiltersForCsv = (
  rows:       Registration[],
  f:          Filters,
  cityScope?: string,
): Registration[] =>
  rows.filter((r) => {
    if (cityScope && r.city?.toLowerCase() !== cityScope.toLowerCase()) return false;
    if (f.search) {
      const q = f.search.toLowerCase();
      if (
        !r.fullName.toLowerCase().includes(q) &&
        !r.email.toLowerCase().includes(q) &&
        !r.city.toLowerCase().includes(q) &&
        !r.primaryRole.toLowerCase().includes(q)
      ) return false;
    }
    if (f.gender !== "all" && r.gender.toLowerCase() !== f.gender.toLowerCase()) return false;
    if (f.currentStatus !== "all" && r.currentStatus.toLowerCase() !== f.currentStatus.toLowerCase()) return false;
    if (f.appStatus !== "all" && (r.status ?? "pending") !== f.appStatus) return false;
    if (f.primaryRole !== "all" && r.primaryRole !== f.primaryRole) return false;
    if (f.city !== "all" && r.city !== f.city) return false;
    if (f.isPitchingIdea !== "all" && r.isPitchingIdea !== (f.isPitchingIdea === "true")) return false;
    if (f.requestingScholarship !== "all" && (r.requestingScholarship ?? false) !== (f.requestingScholarship === "true")) return false;
    return true;
  });

const DEFAULT_FILTERS: Filters = {
  search:                "",
  gender:                "all",
  currentStatus:         "all",
  isPitchingIdea:        "all",
  requestingScholarship: "all",
  city:                  "all",
  appStatus:             "all",
  primaryRole:           "all",
};

// Build chart entries from stats (adds percentage)
function buildChartData(stats: RegistrationStatsResponse) {
  const total = stats.total;
  const ideaThemeTotal = stats.byIdeaTheme.reduce((a, e) => a + e.count, 0);
  return {
    city:     toChartEntries(stats.byCity,   total),
    gender:   toChartEntries(stats.byGender, total),
    ideaTheme: toChartEntries(stats.byIdeaTheme, ideaThemeTotal),
    idea: [
      { name: "Pitching",     value: stats.pitchingIdea,           percentage: total ? ((stats.pitchingIdea / total) * 100).toFixed(1) : "0" },
      { name: "Not Pitching", value: total - stats.pitchingIdea,   percentage: total ? (((total - stats.pitchingIdea) / total) * 100).toFixed(1) : "0" },
    ].filter((e) => e.value > 0),
    scholarship: [
      { name: "Requesting",     value: stats.requestingScholarship,         percentage: total ? ((stats.requestingScholarship / total) * 100).toFixed(1) : "0" },
      { name: "Not Requesting", value: total - stats.requestingScholarship, percentage: total ? (((total - stats.requestingScholarship) / total) * 100).toFixed(1) : "0" },
    ].filter((e) => e.value > 0),
  };
}

export default function App() {
  const qc       = useQueryClient();
  const { user } = useAuth();

  const [activeView, setActiveView] = useState<ActiveView>("applicants");
  const [filters,    setFilters]    = useState<Filters>(DEFAULT_FILTERS);
  const [visualPage, setVisualPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [attendeesEventId, setAttendeesEventId] = useState<string | null>(null);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);

  const handleViewAttendees = (event: MeetupEvent) => {
    setAttendeesEventId(event.id);
    setActiveView("meetup-attendees");
  };

  const cityScope = user?.role === "city" ? user.city : undefined;

  // ── Stats query: aggregated counts only (one lightweight request)
  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
  } = useRegistrationStats(cityScope);

  const chartData = useMemo(
    () => (stats ? buildChartData(stats) : null),
    [stats],
  );

  // ── Table query: paginated + server-filtered
  const serverFilters = useMemo((): RegistrationsParams => ({
    page:  visualPage,
    limit: LIMIT,
    ...(cityScope ? { city: cityScope } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.gender !== "all" ? { gender: filters.gender } : {}),
    ...(filters.currentStatus !== "all" ? { currentStatus: filters.currentStatus } : {}),
    ...(filters.isPitchingIdea !== "all" ? { isPitchingIdea: filters.isPitchingIdea } : {}),
    ...(filters.requestingScholarship !== "all" ? { requestingScholarship: filters.requestingScholarship } : {}),
    ...(filters.appStatus !== "all" ? { status: filters.appStatus } : {}),
    ...(filters.primaryRole !== "all" ? { primaryRole: filters.primaryRole } : {}),
  }), [filters, visualPage, cityScope]);

  const { data: tableData, isLoading, isFetching, isError, error } = useRegistrations(serverFilters);

  const pageRows = tableData?.data ?? [];
  const meta     = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  // ── Handlers
  const handleFiltersChange = useCallback((f: Filters) => {
    setFilters(f);
    setVisualPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setVisualPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: registrationKeys.all });
    qc.invalidateQueries({ queryKey: statsKeys.registrations(cityScope) });
  }, [qc, cityScope]);

  // ── CSV export: on-demand fetch (uses cache if fresh, otherwise fetches once)
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await qc.fetchQuery({
        queryKey: registrationKeys.list({ page: 1, limit: 5000, ...(cityScope ? { city: cityScope } : {}) }),
        queryFn:  () => getRegistrations({ page: 1, limit: 5000, ...(cityScope ? { city: cityScope } : {}) }),
        staleTime: 30_000,
      });
      exportToCsv(applyFiltersForCsv(result.data, filters, cityScope));
    } finally {
      setIsExporting(false);
    }
  }, [qc, cityScope, filters]);

  const isRefreshing = isFetching || statsFetching;

  // Catalysts get their own self-contained view — none of the admin tabs,
  // stats, or applicant table apply to them. No sidebar for this branch;
  // the catalyst portal is a single view.
  if (user?.role === "catalyst") {
    return (
      <div className="min-h-screen bg-brand-navy">
        <Header onRefresh={handleRefresh} isRefreshing={isRefreshing} />
        <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
          <CatalystPortalView />
        </main>
      </div>
    );
  }

  const isMaster = user?.role === "master";

  return (
    <div className="min-h-screen bg-brand-navy flex">
      {isMaster && (
        <Sidebar
          groups={NAV_GROUPS}
          active={activeView}
          onSelect={setActiveView}
          mobileOpen={sidebarMobileOpen}
          onMobileClose={() => setSidebarMobileOpen(false)}
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <Header
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onMenuClick={isMaster ? () => setSidebarMobileOpen(true) : undefined}
        />

        <main className="max-w-screen-xl w-full mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Mobile-only heading for the active view — replaces the sidebar
            label when the drawer is closed so operators know where they are. */}
        {isMaster && (
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setSidebarMobileOpen(true)}
              className="p-2 rounded-lg bg-brand-card border border-brand-border text-brand-muted hover:text-white"
              aria-label="Open navigation"
            >
              <Menu size={16} />
            </button>
            <p className="text-white font-semibold text-sm">
              {NAV_GROUPS.flatMap((g) => g.items).find((i) => i.key === activeView)?.label ?? "Dashboard"}
            </p>
          </div>
        )}

        {/* ── Mentors view ─────────────────────────────────────────────────── */}
        {activeView === "mentors" && user?.role === "master" && (
          <MentorsView />
        )}

        {/* ── Newsletter view ───────────────────────────────────────────────── */}
        {activeView === "newsletter" && user?.role === "master" && (
          <NewsletterView />
        )}

        {/* ── Report Leads view ─────────────────────────────────────────────── */}
        {activeView === "report-leads" && user?.role === "master" && (
          <ReportLeadsView />
        )}

        {/* ── Community Catalysts view ─────────────────────────────────────── */}
        {activeView === "community-catalysts" && user?.role === "master" && (
          <CommunityCatalystsView />
        )}

        {/* ── Ecosystem Perception view ────────────────────────────────────── */}
        {activeView === "ecosystem-perception" && user?.role === "master" && (
          <EcosystemPerceptionView />
        )}

        {/* ── FNF Survey view ───────────────────────────────────────────────── */}
        {activeView === "fnf-survey" && user?.role === "master" && (
          <FnfSurveyView />
        )}

        {/* ── Catalyst Waitlist view ───────────────────────────────────────── */}
        {activeView === "catalyst-waitlist" && user?.role === "master" && (
          <CatalystWaitlistView />
        )}

        {/* ── Meetup Proposals view (includes published-event stats inline) ─── */}
        {activeView === "meetup-proposals" && user?.role === "master" && (
          <MeetupProposalsView onViewAttendees={handleViewAttendees} />
        )}

        {/* ── Meetup Attendees view ────────────────────────────────────────── */}
        {activeView === "meetup-attendees" && user?.role === "master" && (
          <MeetupAttendeesView initialEventId={attendeesEventId} />
        )}

        {/* ── Meetup Feedback view ─────────────────────────────────────────── */}
        {activeView === "meetup-feedbacks" && user?.role === "master" && (
          <MeetupFeedbacksView />
        )}

        {/* ── Chart Lab view ────────────────────────────────────────────────── */}
        {activeView === "chart-lab" && user?.role === "master" && (
          <ChartLabView />
        )}

        {/* ── GEW & Conference views ─────────────────────────────────────── */}
        {activeView === "gew-overview" && user?.role === "master" && (
          <GewOverviewView />
        )}
        {activeView === "gew-feedback" && user?.role === "master" && (
          <GewFeedbackView />
        )}
        {activeView === "gew-live" && user?.role === "master" && (
          <GewLiveView />
        )}
        {activeView === "gew-participants" && user?.role === "master" && (
          <GewParticipantsView />
        )}
        {activeView === "gew-partners" && user?.role === "master" && (
          <GewPartnersView />
        )}
        {activeView === "gew-partner-applications" && user?.role === "master" && (
          <GewPartnerApplicationsView />
        )}
        {activeView === "gew-sponsors" && user?.role === "master" && (
          <GewSponsorsView />
        )}
        {activeView === "door-codes" && user?.role === "master" && (
          <DoorCodesView />
        )}

        {/* ── Awards views ─────────────────────────────────────────────────── */}
        {activeView === "awards-nominations" && user?.role === "master" && (
          <AwardsNominationsView />
        )}
        {activeView === "awards-invitations" && user?.role === "master" && (
          <AwardsInvitationsView />
        )}

        {/* ── Investor Lounge views ────────────────────────────────────────── */}
        {activeView === "investor-lounge-founders" && user?.role === "master" && (
          <InvestorLoungeFoundersView />
        )}
        {activeView === "investor-lounge-investors" && user?.role === "master" && (
          <InvestorLoungeInvestorsView />
        )}

        {/* ── National Pitching Day views ─────────────────────────────────── */}
        {activeView === "npd-rsvps" && user?.role === "master" && (
          <NpdRsvpsView />
        )}
        {activeView === "npd-waitlist" && user?.role === "master" && (
          <NpdWaitlistView />
        )}
        {activeView === "npd-registrations" && user?.role === "master" && (
          <NpdRegistrationsView />
        )}

        {/* ── Elevate · Damascus 2026 ──────────────────────────────────── */}
        {activeView === "elevate-applications" && user?.role === "master" && (
          <ElevateApplicationsView />
        )}
        {activeView === "elevate-feedback" && user?.role === "master" && (
          <ElevateFeedbackView />
        )}

        {/* ── Applicants view ──────────────────────────────────────────────── */}
        {activeView === "applicants" && (
          <>
            {/* Scoped heading for city users */}
            {user?.role === "city" && (
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-brand-orange rounded-full" />
                <div>
                  <p className="text-brand-muted text-xs">Showing applicants for</p>
                  <h2 className="text-white font-bold text-lg leading-tight">
                    {user.city}
                  </h2>
                </div>
              </div>
            )}

            {/* Stats cards */}
            {stats && (
              <StatsOverview stats={stats} isLoading={statsLoading} />
            )}
            {statsLoading && !stats && (
              <StatsOverview
                stats={{ total: 0, pitchingIdea: 0, requestingScholarship: 0, availableAllDays: 0, byCity: [], byGender: [], byIdeaTheme: [] }}
                isLoading={true}
              />
            )}

            {/* Charts — master only */}
            {!statsLoading && chartData && user?.role === "master" && (
              <>
                <div className="flex flex-col xl:flex-row gap-6">
                  <CityChart   data={chartData.city}   />
                  <GenderChart data={chartData.gender} total={stats?.total} />
                </div>
                <div className="flex flex-col xl:flex-row gap-6">
                  <IdeaChart        data={chartData.idea}       />
                  <ScholarshipChart data={chartData.scholarship} />
                </div>
                <div className="flex gap-6">
                  <IdeaThemeChart
                    data={chartData.ideaTheme}
                    pitchingTotal={stats?.byIdeaTheme.reduce((a, e) => a + e.count, 0)}
                  />
                </div>
              </>
            )}

            {/* Filters + Download */}
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <FilterBar
                  filters={filters}
                  onChange={handleFiltersChange}
                  onReset={handleReset}
                  isMaster={user?.role === "master"}
                />
              </div>
              {user?.role === "master" && (
                <button
                  onClick={handleExport}
                  disabled={isExporting || statsLoading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl
                             bg-brand-card border border-brand-border
                             text-sm font-medium text-brand-muted
                             hover:text-white hover:border-brand-orange/50
                             transition-colors shrink-0 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {isExporting ? "Exporting…" : "Export CSV"}
                </button>
              )}
            </div>

            {/* Table */}
            <div className="card overflow-hidden">
              {isLoading ? (
                <TableSkeleton />
              ) : isError ? (
                <ErrorState
                  message={(error as Error).message}
                  onRetry={handleRefresh}
                />
              ) : pageRows.length === 0 ? (
                <EmptyState />
              ) : (
                <ApplicantsTable registrations={pageRows} isMaster={user?.role === "master"} />
              )}
            </div>

            {/* Pagination */}
            {!isLoading && meta.totalPages > 1 && (
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={meta.total}
                limit={LIMIT}
                onPageChange={setVisualPage}
                isFetching={isFetching}
              />
            )}
          </>
        )}

      </main>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-6">
      <p className="text-red-400 font-medium">{message}</p>
      <button onClick={onRetry} className="btn-primary text-sm">Try Again</button>
    </div>
  );
}

